// Uploads to Firebase Storage with in-browser image compression.

import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../utils/firebase";

export type StorageFolder = "styleframes" | "thumbnails" | "logos" | "videos";

const MAX_DIMENSION: Record<StorageFolder, number> = {
  styleframes: 2400,
  thumbnails: 1920,
  logos: 512,
  videos: 0,
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image illisible"));
    img.src = src;
  });
}

/**
 * Re-encodes an image as WebP, downscaled to `maxDimension`.
 * Keeps the original when it is already smaller (SVG/GIF are never touched).
 */
export async function compressImage(file: Blob, maxDimension: number, quality = 0.86): Promise<Blob> {
  if (!file.type.startsWith("image/") || /svg|gif/.test(file.type)) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const scale = Math.min(1, maxDimension / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/webp", quality),
    );
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function extensionFor(type: string, fallbackName?: string): string {
  const fromType = type.split("/")[1]?.replace("jpeg", "jpg").replace("quicktime", "mov");
  if (fromType && /^[a-z0-9]+$/.test(fromType)) return fromType;
  return fallbackName?.split(".").pop()?.toLowerCase() || "bin";
}

function safeBaseName(name: string): string {
  return (
    name
      .replace(/\.[^.]+$/, "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-zA-Z0-9-_]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "file"
  );
}

/**
 * Uploads a file (compressing images first) and returns its download URL.
 * `onProgress` receives a 0–1 ratio for this file.
 */
export async function uploadMedia(
  folder: StorageFolder,
  file: Blob,
  name = "file",
  onProgress?: (ratio: number) => void,
): Promise<string> {
  const expected = folder === "videos" ? "video/" : "image/";
  if (!file.type.startsWith(expected)) {
    throw new Error(
      folder === "videos" ? "Ce fichier n'est pas une vidéo." : "Ce fichier n'est pas une image (PNG, JPG, WebP, SVG…).",
    );
  }
  const body = MAX_DIMENSION[folder] ? await compressImage(file, MAX_DIMENSION[folder]) : file;
  const path = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2, 7)}_${safeBaseName(name)}.${extensionFor(body.type, name)}`;
  const task = uploadBytesResumable(ref(storage, path), body, {
    contentType: body.type || undefined,
    cacheControl: "public, max-age=31536000, immutable",
  });
  await new Promise<void>((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => onProgress?.(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
      reject,
      () => resolve(),
    );
  });
  onProgress?.(1);
  return getDownloadURL(task.snapshot.ref);
}

/** Uploads files sequentially and reports overall progress (0–1). */
export async function uploadMany(
  folder: StorageFolder,
  files: { blob: Blob; name: string }[],
  onProgress?: (ratio: number) => void,
): Promise<string[]> {
  const urls: string[] = [];
  for (let i = 0; i < files.length; i++) {
    urls.push(
      await uploadMedia(folder, files[i].blob, files[i].name, (r) =>
        onProgress?.((i + r) / files.length),
      ),
    );
  }
  return urls;
}
